// src/components/Cheque/Cheque.jsx

import React, { useState, useEffect, useContext } from 'react';
import { userContext } from '../../context/ContextProvider';
import chequeService from '../../services/chequeService';
import ChequeTable from './ChequeTable';
import ChequeModal from './ChequeModal';
import { showNotification } from '../../utils/notification';
import ConfirmDialog from "../common/ConfirmDialog";
import { PlusCircle } from 'lucide-react';
import './cheque.css';
import '../Inventory/inventory.css';

/**
 * Cheque Component
 * Main page component that manages cheques from cash movements
 * Handles CRUD operations and filtering
 */
const Cheque = ({ toggleSidebar }) => {
  // Get user info from your existing context
  const { role, user } = useContext(userContext);
  
  // ========== STATE MANAGEMENT ==========
  const [allCheques, setAllCheques] = useState([]); // Store ALL cheques
  const [filteredCheques, setFilteredCheques] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCheque, setSelectedCheque] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    inventory: '',
    startDate: '',
    endDate: ''
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Get user role and ID from context
  const userRole = role;
  const userId = user?.id || user?.userId;

  // Debug log
  console.log('Cheque Component - User Info:', { userRole, userId, user, role });

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [chequeToDelete, setChequeToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get unique inventory names for filter dropdown
  const uniqueInventories = [...new Set(allCheques.map(chq => chq.inventory).filter(Boolean))];

  const handleDeleteClick = (cheque) => {
    setChequeToDelete(cheque);
    setIsConfirmOpen(true);
  };

  // ========== LOAD DATA ON COMPONENT MOUNT ==========
  useEffect(() => {
    if (userRole) {
      loadCheques();
    }
  }, [userRole, userId]);

  // Auto-filter when filters or cheques change
  useEffect(() => {
    applyFilters();
    setCurrentPage(1); // Reset to first page when filters change
  }, [allCheques, filters]);

  // ========== DATA LOADING ==========
  const loadCheques = async () => {
    try {
      setIsLoading(true);
      
      if (!userRole) {
        throw new Error('User role is not available');
      }
      
      // Load ALL cheques from cash movements
      const response = await chequeService.getAll(1, 100000, userRole, userId);
      
      // Extract the data array from the response
      const chequesData = response.data || response || [];
      
      setAllCheques(chequesData);
      const roleMessage = userRole === 'superadmin' ? 'all' : 'your';
      showNotification(`Loaded ${chequesData.length} ${roleMessage} cheque(s)`, 'success');
    } catch (error) {
      console.error('Error loading cheques:', error);
      showNotification(`Failed to load cheques: ${error.message}`, 'error');
      setAllCheques([]);
    } finally {
      setIsLoading(false);
    }
  };

  // ========== FILTERING (Client-side) ==========
  const applyFilters = () => {
    const safeCheques = Array.isArray(allCheques) ? allCheques : [];
    let filtered = [...safeCheques];

    // Filter by inventory
    if (filters.inventory) {
      filtered = filtered.filter(chq => chq.inventory === filters.inventory);
    }

    // Search filter (searches in description, reference, and sales person)
    if (filters.search.trim()) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(chq => {
        const description = chq.description?.toLowerCase() || '';
        const reference = chq.reference?.toLowerCase() || '';
        const salesPerson = chq.salesPerson?.toLowerCase() || '';
        const id = String(chq.id || '').toLowerCase();
        
        return description.includes(searchLower) || 
               reference.includes(searchLower) || 
               salesPerson.includes(searchLower) ||
               id.includes(searchLower);
      });
    }

    // Date range filters
    if (filters.startDate) {
      const startDate = new Date(filters.startDate);
      startDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(chq => {
        if (!chq.date) return false;
        const chequeDate = new Date(chq.date);
        chequeDate.setHours(0, 0, 0, 0);
        return chequeDate >= startDate;
      });
    }

    if (filters.endDate) {
      const endDate = new Date(filters.endDate);
      endDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(chq => {
        if (!chq.date) return false;
        const chequeDate = new Date(chq.date);
        return chequeDate <= endDate;
      });
    }

    setFilteredCheques(filtered);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prevFilters => ({
      ...prevFilters,
      [name]: value
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      inventory: '',
      startDate: '',
      endDate: ''
    });
    showNotification('Filters cleared', 'info');
  };

  // ========== MODAL OPERATIONS ==========
  const handleAddNew = () => {
    setSelectedCheque(null);
    setIsModalOpen(true);
  };

  const handleEdit = (cheque) => {
    setSelectedCheque(cheque);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCheque(null);
  };

  // ========== CRUD OPERATIONS ==========
  const handleSave = async (formData) => {
    try {
      setIsSaving(true);

      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      if (selectedCheque) {
        await chequeService.update(selectedCheque.id, payload);
        showNotification(
          `Cheque updated successfully!`,
          'success'
        );
      } else {
        await chequeService.create(payload);
        showNotification(
          `New cheque added successfully!`,
          'success'
        );
      }

      await loadCheques();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving cheque:', error);
      showNotification(
        `Failed to save: ${error.message}`,
        'error'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!chequeToDelete) return;

    try {
      setIsDeleting(true);
      await chequeService.delete(chequeToDelete.id);
      showNotification("Cheque deleted successfully", "success");
      await loadCheques();
    } catch (error) {
      console.error("Delete failed:", error);
      showNotification(`Failed to delete: ${error.message}`, "error");
    } finally {
      setIsDeleting(false);
      setIsConfirmOpen(false);
      setChequeToDelete(null);
    }
  };

  const cancelDelete = () => {
    setIsConfirmOpen(false);
    setChequeToDelete(null);
  };

  // ========== PAGINATION LOGIC ==========
  const totalPages = Math.ceil(filteredCheques.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedCheques = filteredCheques.slice(startIndex, endIndex);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (!userRole) {
    return (
      <div className="cheque-main-content">
        <div className="cheque-loading">Loading user information...</div>
      </div>
    );
  }

  // ========== RENDER ==========
  return (
    <div className="inventory-main-content">
      {/* Page Header */}
      <div className="inventory-content-header">
        <button onClick={toggleSidebar} className="sidebar-toggle-btn" type="button">
          ☰
        </button>
        <div>
          <h1 className="inventory-content-title">Cheque Management</h1>
          {userRole === 'admin' && (
            <p className="cheque-user-info">Viewing your cheques</p>
          )}
          {userRole === 'superadmin' && (
            <p className="cheque-user-info">Viewing all cheques (Super Admin)</p>
          )}
        </div>
        <div className="header-actions">
          <button 
            className="inventory-btn inventory-btn-add" 
            onClick={handleAddNew}
          >
            <PlusCircle size={18} />
            Add New Cheque
          </button>
        </div>
      </div>

      {/* Filter Controls */}
      <div className="cheque-filter-controls">
        <div className="cheque-filter-group">
          <input
            type="text"
            name="search"
            placeholder="Search by description, reference, sales person, or ID..."
            value={filters.search}
            onChange={handleFilterChange}
            className="cheque-custom-input cheque-search-input"
            style={{ flex: 1, minWidth: '300px' }}
          />

          <select
            name="inventory"
            value={filters.inventory}
            onChange={handleFilterChange}
            className="cheque-custom-select"
          >
            <option value="">All Inventories</option>
            {uniqueInventories.map(inv => (
              <option key={inv} value={inv}>{inv}</option>
            ))}
          </select>

          <input
            type="date"
            name="startDate"
            value={filters.startDate}
            onChange={handleFilterChange}
            className="cheque-custom-input cheque-date-input"
            placeholder="Start Date"
          />

          <input
            type="date"
            name="endDate"
            value={filters.endDate}
            onChange={handleFilterChange}
            className="cheque-custom-input cheque-date-input"
            placeholder="End Date"
          />

          <button 
            className="inventory-btn inventory-btn-clear" 
            onClick={handleClearFilters}
          >
            Clear Filters
          </button>
        </div>

        {/* Filter Summary */}
        <div style={{ marginTop: '12px', fontSize: '14px', color: '#666' }}>
          Showing <strong>{filteredCheques.length > 0 ? startIndex + 1 : 0}-{Math.min(endIndex, filteredCheques.length)}</strong> of{' '}
          <strong>{filteredCheques.length}</strong> filtered result{filteredCheques.length !== 1 ? 's' : ''}
          {filteredCheques.length !== allCheques.length && (
            <span> (from <strong>{allCheques.length}</strong> total)</span>
          )}
        </div>
      </div>

      {/* Data Table */}
      <ChequeTable
        cheques={paginatedCheques}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
        isLoading={isLoading}
      />

      {/* Pagination Controls */}
      {filteredCheques.length > 0 && (
        <div className="pagination-controls">
          <button 
            className="btn-page" 
            disabled={currentPage === 1} 
            onClick={handlePreviousPage}
          >
            Previous
          </button>
          
          <div className="page-numbers">
            {currentPage > 2 && (
              <>
                <button className="btn-page-num" onClick={() => handlePageChange(1)}>1</button>
                {currentPage > 3 && <span className="page-ellipsis">...</span>}
              </>
            )}
            
            {currentPage > 1 && (
              <button 
                className="btn-page-num" 
                onClick={() => handlePageChange(currentPage - 1)}
              >
                {currentPage - 1}
              </button>
            )}
            
            <button className="btn-page-num active">{currentPage}</button>
            
            {currentPage < totalPages && (
              <button 
                className="btn-page-num" 
                onClick={() => handlePageChange(currentPage + 1)}
              >
                {currentPage + 1}
              </button>
            )}
            
            {currentPage < totalPages - 1 && (
              <>
                {currentPage < totalPages - 2 && <span className="page-ellipsis">...</span>}
                <button className="btn-page-num" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
              </>
            )}
          </div>

          <span className="page-info">Page {currentPage} of {totalPages || 1}</span>
          
          <button 
            className="btn-page" 
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={handleNextPage}
          >
            Next
          </button>
        </div>
      )}

      {/* Add/Edit Modal */}
      <ChequeModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSave}
        cheque={selectedCheque}
        isSaving={isSaving}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmOpen}
        title="Delete Cheque"
        message={`Are you sure you want to delete this cheque?`}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isProcessing={isDeleting}
      />
    </div>
  );
};

export default Cheque;